/**
 * Local-date helpers for report date pickers.
 *
 * Every function here builds Date objects with the LOCAL constructor
 * (`new Date(y, m - 1, d)`) and formats by reading local getters. It
 * deliberately never touches `toISOString()`, which converts to UTC first and
 * therefore returns tomorrow's date for anyone in Eastern time after 8 PM —
 * the off-by-one that `ReportDateRangeSelector.getDefaultEndDate()` still has.
 *
 * Extracted from SalesTeamFunnelReportModal, which had the correct local
 * versions inline, when the Sales Team Report gained a date-range mode.
 */

/** Local-time today as YYYY-MM-DD. */
export function todayLocal(): string {
  return formatLocal(new Date());
}

/** Format a Date as YYYY-MM-DD using its LOCAL calendar fields. */
export function formatLocal(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    + `-${String(date.getDate()).padStart(2, '0')}`;
}

/** Parse YYYY-MM-DD as a LOCAL midnight Date (never UTC). */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** The 1st of the month containing `dateStr`, as YYYY-MM-DD. */
export function firstOfMonth(dateStr: string): string {
  return `${dateStr.slice(0, 7)}-01`;
}

/** Day-of-month for a YYYY-MM-DD string. */
export function dayOfMonth(dateStr: string): number {
  return parseInt(dateStr.slice(8, 10), 10);
}

/**
 * Inclusive day count of [startDate, endDate]. Uses Date.UTC on the parsed
 * components so the arithmetic is DST-proof — a local-midnight subtraction
 * across a DST boundary is off by an hour and can floor to the wrong day.
 */
export function inclusiveDayCount(startDate: string, endDate: string): number {
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const ms = Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd);
  return Math.floor(ms / 86_400_000) + 1;
}

/** "2026-08-25" → "Mon 8/25/2026". */
export function formatReportDate(dateStr: string): string {
  const date = parseLocalDate(dateStr);
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
  return `${dow} ${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

/** "2026-06-01" → "Jun 1, 2026". */
export function formatLongDate(dateStr: string): string {
  const date = parseLocalDate(dateStr);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
  return `${mon} ${date.getDate()}, ${date.getFullYear()}`;
}

/** "2026-08-01" → "Aug 1" (no year) for compact range summaries. */
export function formatShortDate(dateStr: string): string {
  const date = parseLocalDate(dateStr);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
  return `${mon} ${date.getDate()}`;
}
